import { useDispatch, useSelector } from 'react-redux';
import { setCalculateTaxLoading, setTaxCalculationId } from '../state/calculateTaxSlice';
import { TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES, calculateTax } from '../data/CalculateTaxApi';
import Raven from 'raven-js';
import debounce from 'hs-lodash/debounce';
import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { getBuyerOverridesLineItems } from '../state/BuyerOverrides/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
const DEBOUNCE_TIME = 1000;
export function getShouldCalculateTaxOnAddrFieldChange(fieldName, fieldValue, address, checkoutSession) {
  // Only calculate tax if automated sales tax is enabled
  if (!(checkoutSession !== null && checkoutSession !== void 0 && checkoutSession.isAutomatedSalesTaxEnabled)) {
    return false;
  }
  const isPostalCode = fieldName === 'postalCode';
  const isCountry = fieldName === 'country';
  if (!isPostalCode && !isCountry) {
    return false;
  }
  const isEmpty = fieldValue === '' || isCountry && address.postalCode === '' || isPostalCode && address.country === '';
  return !isEmpty;
}
export function getShouldCalculateTaxOnSubtotalChange({
  isAutomatedSalesTaxEnabled,
  billingPostalCode,
  shippingPostalCode,
  billingCountry,
  shippingCountry
}) {
  if (!isAutomatedSalesTaxEnabled) {
    return false;
  }
  const hasShippingCountryAndPostalCode = shippingCountry.length > 0 && shippingPostalCode.length > 0;
  const hasBillingCountryAndPostalCode = billingCountry.length > 0 && billingPostalCode.length > 0;
  return hasShippingCountryAndPostalCode || hasBillingCountryAndPostalCode;
}
const convertAddressToTaxCalcForm = address => ({
  line1: address.address,
  line2: address.address2,
  postalCode: address.postalCode,
  country: address.country,
  city: address.city,
  state: address.state
});
export const getCalculateTaxRequestData = (checkoutSession, buyerOverridesLineItems, formState) => {
  const billingAddress = formState.billingAddress;
  const shippingAddress = formState.shippingAddress;
  const shouldCalculateTaxForShippingAddress = shippingAddress.country.length > 0 && shippingAddress.postalCode.length > 0;
  const shouldCalculateTaxForBillingAddress = billingAddress.country.length > 0 && billingAddress.postalCode.length > 0;
  return {
    buyerOverrideLineItems: buyerOverridesLineItems,
    shippingAddress: shouldCalculateTaxForShippingAddress ? convertAddressToTaxCalcForm(shippingAddress) : undefined,
    billingAddress: shouldCalculateTaxForBillingAddress ? convertAddressToTaxCalcForm(billingAddress) : undefined
  };
};
const useCalculateTax = () => {
  const dispatch = useDispatch();
  const {
    dispatch: formDispatch,
    checkoutSession,
    formState
  } = usePaymentFormContext();
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const callCalculateTax = async formStateOverride => {
    try {
      FloatingAlertStore.removeAlert('calculateTaxError');
      const {
        taxCalculationId
      } = await calculateTax({
        checkoutSessionId: checkoutSession.id,
        data: getCalculateTaxRequestData(checkoutSession, buyerOverridesLineItems, Object.assign({}, formState, formStateOverride))
      });
      dispatch(setTaxCalculationId(taxCalculationId));
      return taxCalculationId;
    } catch (error) {
      dispatch(setCalculateTaxLoading(false));
      dispatch(setTaxCalculationId(null));
      if (error instanceof Error) {
        Raven.captureException(error);
      }
      if (typeof error === 'object' && error !== null && 'data' in error && typeof error.data === 'string') {
        try {
          const errorData = JSON.parse(error.data);
          switch (errorData.subCategory) {
            case TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES.INVALID_CUSTOMER_TAX_LOCATION:
            case TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES.INVALID_SHIPPING_ADDRESS:
            case TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES.INVALID_TAX_LOCATION:
              FloatingAlertStore.addAlert({
                id: 'calculateTaxError',
                titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.enterValidAddress.title"
                }),
                message: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.enterValidAddress.message"
                }),
                type: 'danger'
              });
              break;
            case TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES.MUST_PROVIDE_POSTAL_CODE:
              FloatingAlertStore.addAlert({
                id: 'calculateTaxError',
                titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.missingPostalCode.title"
                }),
                message: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.missingPostalCode.message"
                }),
                type: 'danger'
              });
              break;
            case TAX_CALCULATION_CREATE_ERROR_SUBCATEGORIES.TOTAL_AFTER_TAX_OVER_LIMIT:
              formDispatch({
                type: 'setPageError',
                data: true
              });
              break;
            default:
              FloatingAlertStore.addAlert({
                id: 'calculateTaxError',
                titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.unknownError.title"
                }),
                message: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "payments.errorAlert.calculateTax.unknownError.message"
                }),
                type: 'danger'
              });
              break;
          }
        } catch (alertParsingError) {
          if (alertParsingError instanceof Error) {
            Raven.captureException(alertParsingError);
          }
        }
      }
    }
  };

  // separated out due to how loading works when we call subtotals after a tax calculation
  const callCalculateTaxWithLoading = async formStateOverride => {
    dispatch(setCalculateTaxLoading(true));
    await callCalculateTax(formStateOverride);
  };
  return {
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo
    callCalculateTax: useCallback(callCalculateTax, [buyerOverridesLineItems, checkoutSession, formState]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    debouncedCallCalculateTax: useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce(callCalculateTaxWithLoading, DEBOUNCE_TIME), [buyerOverridesLineItems])
  };
};
export default useCalculateTax;